import { useState, useCallback, useEffect, useContext } from "react";
import { AppContext } from "../store";
import { apiRequest } from '../utils/authConfig';
import { OrgTypes } from "../store/actions";
import useIsFullyAuthenticated from "./useIsFullyAuthenticated";

const api = process.env.REACT_APP_BACKEND_URL;
const apiKey = process.env.REACT_APP_BACKEND_KEY;

export const fetchClaims = async (token: string, idToken: string) => {
    try {
        const response = await fetch(`${api}/user/claims`, {
            method: 'GET',
            headers: [
                ['Authorization', `Bearer ${idToken}`],
                ['AccessToken', `${token}`],
                ['X-Ducks-Volunteer-Subscription-Key', `${apiKey}`]
            ]
        });
        return await response.json();
    } catch (err:any) {
        console.error(err);
        throw new Error(err);
    }
}

const useIsAuthorized = () => {
    const { isAuthenticated, login, logout, user, getToken, authenticationChecked } = useIsFullyAuthenticated();
    const { dispatch, state } = useContext(AppContext);
    const [isAuthorized, setValue] = useState(!!(isAuthenticated && state.orgInfo.roles));
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const setIsAuthorized = useCallback((authorized: boolean) => {
        setValue(authorized);
    }, []);
    const claimsError = state.orgInfo.error;
    useEffect(() => {
        if (claimsError) return;
        if (isAuthenticated && !loading && !isAuthorized) {
            const tokenRequest = { ...apiRequest };
            setLoading(true);
            dispatch({
                type: OrgTypes.setProfile,
                payload: { loading: true, error: false },
            });
            (async () => {
                try {
                    const account = user;
                    const { accessToken, idToken } = await getToken({ ...tokenRequest, account });
                    const unauthorizedUser =!accessToken || !idToken;
                    if (unauthorizedUser) {
                        throw new Error("Unauthorized User.");
                    }
                    const claims = await fetchClaims(accessToken, idToken);
                    const noOrgClaims = claims?.statusCode === 401;
                    if (noOrgClaims) {
                        throw new Error("Unauthorized User.");
                    }
                    dispatch({
                        type: OrgTypes.setProfile,
                        payload: { ...claims, loading: false },
                    });
                    setIsAuthorized(true);
                } catch (err: any) {
                    console.error(JSON.stringify(err));
                    dispatch({
                        type: OrgTypes.setProfile,
                        payload: { loading: false, error: true },
                    });
                    setIsAuthorized(false);
                } finally {
                    setLoading(false);
                    setInitialized(true);
                }
            })();
        }
    }, [user, dispatch, initialized, isAuthenticated, isAuthorized, loading, setIsAuthorized, getToken, claimsError]);

    useEffect(() => {
        // Initialized, but not logged in.
        if (authenticationChecked && !isAuthenticated) {
            setLoading(false);
            setInitialized(true);
        }
    }, [authenticationChecked, isAuthenticated]);
    return { isAuthorized, user, isAuthenticated, loading: loading || !initialized, login, logout, claimsError };
}

export default useIsAuthorized;